import { useCallback, useEffect, useState } from "react";
import { Download, Boxes, ClipboardList, IndianRupee, Recycle, Loader2, X, Warehouse } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formTypeLabel } from "../lib/formTypes";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { PageHeader } from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/common/SearchableSelect";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const ALL = "__all__";

const MAT_STATUSES = [
  "GRN_PENDING", "GRN_COMPLETED", "DEFECTIVE_UNDER_REVIEW", "SENT_BACK_TO_CLIENT", "ACCEPTED_BY_CLIENT",
  "ASSIGNED_TO_FIELD_USER", "ACKNOWLEDGED_BY_FIELD_USER", "RETURN_REQUESTED", "INSTALLED_BY_FIELD_USER", "COMPLETED_DATA_ENTRY",
];
const DE_STATUSES = ["DRAFT", "COMPLETED"];
const lbl = (s) => (s || "").replace(/_/g, " ");

export default function Reports() {
  const { has } = useAuth();
  const { ctx } = useWorkspace();
  const pid = ctx.project?.id || "";
  const [mat, setMat] = useState(null);
  const [de, setDe] = useState(null);
  const [omr, setOmr] = useState(null);
  const [bill, setBill] = useState(null);
  const [busy, setBusy] = useState("");
  const [types, setTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [matProject, setMatProject] = useState("");
  const [deProject, setDeProject] = useState("");
  const [omrProject, setOmrProject] = useState("");
  const [matUpFrom, setMatUpFrom] = useState(""); const [matUpTo, setMatUpTo] = useState("");
  const [deInsFrom, setDeInsFrom] = useState(""); const [deInsTo, setDeInsTo] = useState("");
  const [deEntFrom, setDeEntFrom] = useState(""); const [deEntTo, setDeEntTo] = useState("");
  const [inclInactive, setInclInactive] = useState(false);

  // Filters that scope both the on-screen summary and the export request.
  const [matStatus, setMatStatus] = useState("");
  const [matType, setMatType] = useState("");
  const [deStatus, setDeStatus] = useState("");
  const [deFormType, setDeFormType] = useState("");
  const [omrStatus, setOmrStatus] = useState("");

  // Store-wise Material Flow
  const [flowStores, setFlowStores] = useState([]);
  const [flowStore, setFlowStore] = useState("");
  const [flowType, setFlowType] = useState("");
  const [flowStatus, setFlowStatus] = useState("");
  const [flow, setFlow] = useState(null);

  useEffect(() => {
    if (has("REPORT.MATERIAL.EXPORT")) api.get("/reports/material-flow/stores").then((r) => setFlowStores(r.data.items || [])).catch(() => {});
  }, [has]);

  useEffect(() => {
    if (!flowStore) { setFlow(null); return; }
    api.get("/reports/material-flow/summary", { params: { store_id: flowStore, ...(flowType ? { material_type_id: flowType } : {}), ...(flowStatus ? { current_status: flowStatus } : {}) } })
      .then((r) => setFlow(r.data)).catch(() => setFlow(null));
  }, [flowStore, flowType, flowStatus]);

  useEffect(() => {
    api.get("/material-types", { params: { page_size: 500 } }).then((r) => setTypes(r.data.items || [])).catch(() => {});
    api.get("/projects", { params: { page_size: 500 } }).then((r) => setProjects(r.data.items || [])).catch(() => {});
  }, []);

  const base = useCallback(() => (pid ? { project_id: pid } : {}), [pid]);
  const projOpts = projects.map((p) => ({ value: p.id, label: p.project_code ? `${p.project_code} — ${p.project_name}` : p.project_name }));
  const matParams = useCallback(() => ({ ...((matProject || pid) ? { project_id: matProject || pid } : {}), ...(matStatus ? { current_status: matStatus } : {}), ...(matType ? { material_type_id: matType } : {}), ...(matUpFrom ? { upload_from: matUpFrom } : {}), ...(matUpTo ? { upload_to: matUpTo } : {}), ...(inclInactive ? { include_inactive: true } : {}) }), [matProject, pid, matStatus, matType, matUpFrom, matUpTo, inclInactive]);
  const deParams = useCallback(() => ({ ...((deProject || pid) ? { project_id: deProject || pid } : {}), ...(deStatus ? { status: deStatus } : {}), ...(deFormType ? { form_type: deFormType } : {}), ...(deInsFrom ? { install_from: deInsFrom } : {}), ...(deInsTo ? { install_to: deInsTo } : {}), ...(deEntFrom ? { entry_from: deEntFrom } : {}), ...(deEntTo ? { entry_to: deEntTo } : {}), ...(inclInactive ? { include_inactive: true } : {}) }), [deProject, pid, deStatus, deFormType, deInsFrom, deInsTo, deEntFrom, deEntTo, inclInactive]);
  const omrParams = useCallback(() => ({ ...((omrProject || pid) ? { project_id: omrProject || pid } : {}), ...(omrStatus ? { status: omrStatus } : {}), ...(inclInactive ? { include_inactive: true } : {}) }), [omrProject, pid, omrStatus, inclInactive]);

  const load = useCallback(() => {
    api.get("/reports/material/summary", { params: matParams() }).then((r) => setMat(r.data)).catch(() => {});
    api.get("/reports/data-entry/summary", { params: deParams() }).then((r) => setDe(r.data)).catch(() => {});
    api.get("/reports/old-meter/summary", { params: omrParams() }).then((r) => setOmr(r.data)).catch(() => {});
    if (has("REPORT.BILLING.VIEW")) api.get("/reports/billing/summary", { params: base() }).then((r) => setBill(r.data)).catch(() => {});
  }, [matParams, deParams, omrParams, base, has]);
  useEffect(() => { load(); }, [load]);

  const filtersFor = (report_type) => {
    if (report_type === "MATERIAL_STATUS") return matParams();
    if (report_type === "DATA_ENTRY") return deParams();
    if (report_type === "OLD_METER_RETURN") return omrParams();
    if (report_type === "MATERIAL_FLOW") return { store_id: flowStore, ...(flowType ? { material_type_id: flowType } : {}), ...(flowStatus ? { current_status: flowStatus } : {}) };
    return base();
  };

  const requestExport = async (report_type) => {
    setBusy(report_type);
    try {
      const { data } = await api.post("/reports/exports", { report_type, filters: filtersFor(report_type), idempotency_key: crypto.randomUUID() });
      if (data.duplicate === "in_progress") toast.warning("A matching report is already being prepared — a fresh copy has been requested anyway. Check Download Centre.");
      else if (data.duplicate === "ready") toast.warning("A matching report already exists in Download Centre — a fresh copy is being prepared anyway.");
      else toast.success("Export request submitted. The completed file will appear in Download Centre.");
    } catch (e) { toast.error(e.response?.data?.detail || "Request failed"); }
    finally { setBusy(""); }
  };

  const ExportBtn = ({ type }) => (
    <Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid={`export-${type}`} onClick={() => requestExport(type)} disabled={busy === type}>
      {busy === type ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Request Export
    </Button>
  );

  const typeNameToId = (name) => types.find((t) => t.type_name === name)?.id || "";
  const matTypeName = types.find((t) => t.id === matType)?.type_name || "";

  return (
    <div>
      <PageHeader title="Reports" subtitle="Filter the data, then request an Excel export (delivered to Download Centre)"
        actions={<div className="flex items-center gap-3">
          {!pid && (
            <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-slate-600" data-testid="reports-include-inactive">
              <input type="checkbox" checked={inclInactive} onChange={(e) => setInclInactive(e.target.checked)} className="h-3.5 w-3.5 accent-[var(--mf-purple-600)]" />
              Include inactive projects
            </label>
          )}
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm" data-testid="reports-active-project"><span className="text-xs text-slate-500">Active Project:</span> <span className="font-semibold text-blue-800">{ctx.project ? (ctx.project.project_code ? `${ctx.project.project_code} — ` : "") + ctx.project.project_name : "All Projects"}</span></div>
        </div>} />

      <Tabs defaultValue="material">
        <div data-testid="export-limit-note" className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <span>Exports are limited to 250,000 records per request. Use Project, Status and Type filters below to narrow the data you download.</span>
        </div>
        <TabsList>
          <TabsTrigger value="material" data-testid="rep-tab-material"><Boxes className="mr-1 h-4 w-4" /> Material</TabsTrigger>
          <TabsTrigger value="data-entry" data-testid="rep-tab-de"><ClipboardList className="mr-1 h-4 w-4" /> Data Entry</TabsTrigger>
          <TabsTrigger value="old-meter" data-testid="rep-tab-omr"><Recycle className="mr-1 h-4 w-4" /> Old Meter</TabsTrigger>
          {has("REPORT.MATERIAL.EXPORT") && <TabsTrigger value="flow" data-testid="rep-tab-flow"><Warehouse className="mr-1 h-4 w-4" /> Store Flow</TabsTrigger>}
          {has("REPORT.BILLING.VIEW") && <TabsTrigger value="billing" data-testid="rep-tab-billing"><IndianRupee className="mr-1 h-4 w-4" /> Billing</TabsTrigger>}
        </TabsList>

        {/* MATERIAL */}
        <TabsContent value="material" className="mt-4 space-y-4" data-testid="rep-material">
          <FilterBar onClear={(matStatus || matType || matProject || matUpFrom || matUpTo) ? () => { setMatStatus(""); setMatType(""); setMatProject(""); setMatUpFrom(""); setMatUpTo(""); } : null}>
            <div className="w-60"><Label className="text-xs text-slate-500">By Project</Label><div className="mt-1"><SearchableSelect testid="mat-project-filter" value={matProject} onChange={setMatProject} options={projOpts} placeholder="All my projects" /></div></div>
            <div><Label className="text-xs text-slate-500">Upload From</Label><input type="date" data-testid="mat-upload-from" value={matUpFrom} onChange={(e) => setMatUpFrom(e.target.value)} className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" /></div>
            <div><Label className="text-xs text-slate-500">Upload To</Label><input type="date" data-testid="mat-upload-to" value={matUpTo} onChange={(e) => setMatUpTo(e.target.value)} className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" /></div>
            <FilterSelect testid="mat-status-filter" label="By Status" value={matStatus} onChange={setMatStatus}
              options={MAT_STATUSES.map((s) => ({ value: s, label: lbl(s) }))} />
            <FilterSelect testid="mat-type-filter" label="By Type" value={matType} onChange={setMatType}
              options={types.map((t) => ({ value: t.id, label: t.type_name }))} />
          </FilterBar>
          <Card title="Materials (matching filters)" value={mat?.total} actions={<><ExportBtn type="MATERIAL_STATUS" /><Button size="sm" variant="outline" data-testid="export-MATERIAL_EVENTS" onClick={() => requestExport("MATERIAL_EVENTS")}>Lifecycle Export</Button></>} />
          <Breakdown title="By Status" data={mat?.by_status} activeKey={matStatus} onSelect={(k) => setMatStatus(k === matStatus ? "" : k)} />
          <Breakdown title="By Type" data={mat?.by_type} activeKey={matTypeName} onSelect={(name) => { const id = typeNameToId(name); setMatType(id === matType ? "" : id); }} />
        </TabsContent>

        {/* DATA ENTRY */}
        <TabsContent value="data-entry" className="mt-4 space-y-4" data-testid="rep-de">
          <FilterBar onClear={(deStatus || deFormType || deProject || deInsFrom || deInsTo || deEntFrom || deEntTo) ? () => { setDeStatus(""); setDeFormType(""); setDeProject(""); setDeInsFrom(""); setDeInsTo(""); setDeEntFrom(""); setDeEntTo(""); } : null}>
            <div className="w-60"><Label className="text-xs text-slate-500">By Project</Label><div className="mt-1"><SearchableSelect testid="de-project-filter" value={deProject} onChange={setDeProject} options={projOpts} placeholder="All my projects" /></div></div>
            <div><Label className="text-xs text-slate-500">Install From</Label><input type="date" data-testid="de-install-from" value={deInsFrom} onChange={(e) => setDeInsFrom(e.target.value)} className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" /></div>
            <div><Label className="text-xs text-slate-500">Install To</Label><input type="date" data-testid="de-install-to" value={deInsTo} onChange={(e) => setDeInsTo(e.target.value)} className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" /></div>
            <div><Label className="text-xs text-slate-500">Entry Date From</Label><input type="date" data-testid="de-entry-from" value={deEntFrom} onChange={(e) => setDeEntFrom(e.target.value)} className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" /></div>
            <div><Label className="text-xs text-slate-500">Entry Date To</Label><input type="date" data-testid="de-entry-to" value={deEntTo} onChange={(e) => setDeEntTo(e.target.value)} className="mt-1 block h-9 rounded-md border border-slate-200 px-2 text-sm" /></div>
            <FilterSelect testid="de-status-filter" label="By Status" value={deStatus} onChange={setDeStatus}
              options={DE_STATUSES.map((s) => ({ value: s, label: lbl(s) }))} />
            <FilterSelect testid="de-formtype-filter" label="By Form Type" value={deFormType} onChange={setDeFormType}
              options={Object.keys(de?.by_form_type || {}).map((s) => ({ value: s, label: formTypeLabel(s) }))} />
          </FilterBar>
          <div className="grid gap-3 sm:grid-cols-4">
            <Stat label="Total" value={de?.total} /><Stat label="Completed" value={de?.completed} />
            <Stat label="Drafts" value={de?.drafts} /><Stat label="Completed Today" value={de?.completed_today} />
          </div>
          <Card title="Materials Consumed" value={de?.materials_consumed} actions={<ExportBtn type="DATA_ENTRY" />} />
          <Breakdown title="By Form Type" data={de?.by_form_type} activeKey={deFormType} labeler={formTypeLabel} onSelect={(k) => setDeFormType(k === deFormType ? "" : k)} />
        </TabsContent>

        {/* OLD METER */}
        <TabsContent value="old-meter" className="mt-4 space-y-4" data-testid="rep-omr">
          <FilterBar onClear={(omrStatus || omrProject) ? () => { setOmrStatus(""); setOmrProject(""); } : null}>
            <div className="w-60"><Label className="text-xs text-slate-500">By Project</Label><div className="mt-1"><SearchableSelect testid="omr-project-filter" value={omrProject} onChange={setOmrProject} options={projOpts} placeholder="All my projects" /></div></div>
            <FilterSelect testid="omr-status-filter" label="By Status" value={omrStatus} onChange={setOmrStatus}
              options={Object.keys(omr?.by_status || {}).map((s) => ({ value: s, label: lbl(s) }))} />
          </FilterBar>
          <Card title="Old Meter Returns (matching filters)" value={omr?.total} actions={<ExportBtn type="OLD_METER_RETURN" />} />
          <Breakdown title="By Status" data={omr?.by_status} activeKey={omrStatus} onSelect={(k) => setOmrStatus(k === omrStatus ? "" : k)} />
        </TabsContent>

        {has("REPORT.MATERIAL.EXPORT") && (
        <TabsContent value="flow" className="mt-4 space-y-4" data-testid="rep-flow">
          <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
            <div className="w-72">
              <Label className="text-xs text-slate-500">Store <span className="text-red-500">*</span></Label>
              <Select value={flowStore || ALL} onValueChange={(v) => setFlowStore(v === ALL ? "" : v)}>
                <SelectTrigger data-testid="flow-store-select" className="mt-1 h-9"><SelectValue placeholder="Select a store" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL} disabled>Select a store</SelectItem>
                  {flowStores.map((s) => <SelectItem key={s.id} value={s.id}>{(s.store_code ? s.store_code + " — " : "") + s.store_name}{s.project_name && s.project_name !== "—" ? ` (${s.project_name})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <FilterSelect testid="flow-type-filter" label="By Type" value={flowType} onChange={setFlowType} options={types.map((t) => ({ value: t.id, label: t.type_name }))} />
            <FilterSelect testid="flow-status-filter" label="By Status" value={flowStatus} onChange={setFlowStatus} options={MAT_STATUSES.map((s) => ({ value: s, label: lbl(s) }))} />
            {(flowType || flowStatus) && <Button variant="ghost" size="sm" className="text-slate-500" data-testid="flow-clear" onClick={() => { setFlowType(""); setFlowStatus(""); }}><X className="mr-1 h-3.5 w-3.5" /> Clear</Button>}
          </div>
          {!flowStore ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500" data-testid="flow-empty">Select a store to see its real-time material status and request the full flow export (who/when uploaded, GRN'd, acknowledged, installed & more).</div>
          ) : (
            <>
              <Card title="Materials in this store (matching filters)" value={flow?.total} actions={<ExportBtn type="MATERIAL_FLOW" />} />
              <Breakdown title="By Type" data={flow?.by_type} activeKey={types.find((t) => t.id === flowType)?.type_name || ""} onSelect={(name) => { const id = types.find((t) => t.type_name === name)?.id || ""; setFlowType(id === flowType ? "" : id); }} />
              <Breakdown title="By Status" data={flow?.by_status} activeKey={flowStatus} onSelect={(k) => setFlowStatus(k === flowStatus ? "" : k)} />
            </>
          )}
        </TabsContent>
        )}

        {has("REPORT.BILLING.VIEW") && (
          <TabsContent value="billing" className="mt-4 space-y-4" data-testid="rep-billing">
            <div className="grid gap-3 sm:grid-cols-2">
              <Stat label="Completed Entries" value={bill?.total_entries} />
              <Stat label="Total Amount" value={bill ? money(bill.total_amount) : "—"} />
            </div>
            <Card title="Billing Summary (frozen snapshots)" value="" actions={has("REPORT.BILLING.EXPORT") && <ExportBtn type="BILLING_SUMMARY" />} />
            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <h3 className="mb-2 text-sm font-semibold">By Final Remark</h3>
              {Object.entries(bill?.by_final_remark || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b py-1.5 text-sm last:border-0"><span>{k} <span className="text-slate-400">({v.count})</span></span><span className="font-medium">{money(v.amount)}</span></div>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function FilterBar({ children, onClear }) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid="report-filter-bar">
      {children}
      {onClear && (
        <Button variant="ghost" size="sm" className="text-slate-500" data-testid="report-clear-filters" onClick={onClear}>
          <X className="mr-1 h-3.5 w-3.5" /> Clear
        </Button>
      )}
    </div>
  );
}

function FilterSelect({ testid, label, value, onChange, options }) {
  return (
    <div className="w-52">
      <Label className="text-xs text-slate-500">{label}</Label>
      <Select value={value || ALL} onValueChange={(v) => onChange(v === ALL ? "" : v)}>
        <SelectTrigger data-testid={testid} className="mt-1 h-9"><SelectValue placeholder="All" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Card({ title, value, actions }) {
  return <div className="flex items-center justify-between rounded-lg border bg-white p-4 shadow-sm">
    <div><div className="text-xs uppercase tracking-wide text-slate-400">{title}</div>{value !== "" && <div className="text-2xl font-semibold text-slate-800">{value ?? "—"}</div>}</div>
    <div className="flex gap-2">{actions}</div></div>;
}

function Stat({ label, value }) {
  return <div className="rounded-lg border bg-white p-4 shadow-sm"><div className="text-xs uppercase tracking-wide text-slate-400">{label}</div><div className="text-2xl font-semibold text-slate-800">{value ?? "—"}</div></div>;
}

function Breakdown({ title, data, onSelect, activeKey, labeler }) {
  const entries = Object.entries(data || {});
  const slug = title.replace(/\s+/g, "-").toLowerCase();
  const show = labeler || lbl;
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {onSelect && <span className="text-xs text-slate-400">(tap to filter)</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {entries.map(([k, v]) => {
          const active = activeKey === k;
          if (!onSelect) return <Badge key={k} variant="outline" className="text-xs">{show(k)}: <span className="ml-1 font-semibold">{v}</span></Badge>;
          return (
            <button key={k} type="button" data-testid={`breakdown-${slug}-${k}`} onClick={() => onSelect(k)}
              className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${active ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50"}`}>
              {show(k)}: <span className="font-semibold">{v}</span>
            </button>
          );
        })}
        {entries.length === 0 && <span className="text-sm text-slate-400">No data</span>}
      </div>
    </div>
  );
}
